'use client';

import { memo } from 'react';
import { Building2, Check, Shield } from 'lucide-react';
import { DropdownMenuItem } from '@/components/ui/dropdown-menu';
import { IWorkspaceListItem } from '@/types/workspace';
import TrialWorkspaceIndicator from '../../TrialWorkspaceIndicator';

interface WorkspaceMenuItemProps {
  workspace: IWorkspaceListItem;
  isActive: boolean;
  isSuperAdmin: boolean;
  isTrialWorkspace: boolean;
  trialData?: {
    daysRemaining: number;
    creditsRemaining: number;
  };
  onSwitch: (workspaceId: number) => void;
  isSwitching: boolean;
  navigation?: boolean;
}

const WorkspaceMenuItemComponent = ({
  workspace,
  isActive,
  isSuperAdmin,
  isTrialWorkspace,
  trialData,
  onSwitch,
  isSwitching,
  navigation = false,
}: WorkspaceMenuItemProps) => {
  return (
    <DropdownMenuItem
      onClick={() => onSwitch(workspace.vendorid)}
      disabled={isSwitching}
      className={`${navigation ? 'mbn-workspace-item' : ''} cursor-pointer rounded-lg p-3 transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
        isSuperAdmin ? 'hover:bg-purple-50' : 'hover:bg-gray-50'
      }`}
    >
      <div className='flex items-center gap-3 w-full'>
        <div className={`mbn-workspace-icon p-1.5 rounded-lg relative ${isSuperAdmin ? 'bg-purple-100' : 'bg-blue-100'}`}>
          <Building2 className={`h-4 w-4 ${isSuperAdmin ? 'text-purple-600' : 'text-blue-600'}`} />
          {isSuperAdmin && (
            <div className='absolute -top-1 -right-1 w-3.5 h-3.5 bg-purple-500 rounded-full flex items-center justify-center'>
              <Shield className='h-2 w-2 text-white' />
            </div>
          )}
        </div>
        <div className='flex-1 truncate'>
          <div className='flex items-center gap-2 mb-1'>
            <span className='font-medium text-gray-900 truncate'>
              {workspace.workspace_name || workspace.vendorname || `Workspace ${workspace.vendorid}`}
            </span>
            {isSuperAdmin && (
              <span className='px-1.5 py-0.5 text-[10px] font-medium bg-purple-100 text-purple-700 rounded'>
                Super Admin
              </span>
            )}
            {isTrialWorkspace && trialData && (
              <TrialWorkspaceIndicator
                isTrialActive={true}
                daysRemaining={trialData.daysRemaining}
                creditsRemaining={trialData.creditsRemaining}
                variant="badge"
                showCredits={false}
              />
            )}
          </div>
          <div className='text-xs text-gray-500'>{workspace.member_count} members</div>
        </div>
        {isActive && <Check className='h-4 w-4 text-green-600' />}
      </div>
    </DropdownMenuItem>
  );
};

// Memoize to prevent re-renders when parent re-renders but props haven't changed
export const WorkspaceMenuItem = memo(WorkspaceMenuItemComponent, (prevProps, nextProps) => {
  return (
    prevProps.workspace.vendorid === nextProps.workspace.vendorid &&
    prevProps.navigation === nextProps.navigation &&
    prevProps.isActive === nextProps.isActive &&
    prevProps.isSuperAdmin === nextProps.isSuperAdmin &&
    prevProps.isTrialWorkspace === nextProps.isTrialWorkspace &&
    prevProps.isSwitching === nextProps.isSwitching &&
    prevProps.trialData?.daysRemaining === nextProps.trialData?.daysRemaining &&
    prevProps.trialData?.creditsRemaining === nextProps.trialData?.creditsRemaining
  );
});

WorkspaceMenuItem.displayName = 'WorkspaceMenuItem';
